import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SPIELTYPEN } from "@/lib/matchStats";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Shirt } from "lucide-react";
import { toast } from "sonner";

export default function MatchCreate() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [datum, setDatum] = useState("");
  const [gegner, setGegner] = useState("");
  const [spieltyp, setSpieltyp] = useState("pflichtspiel");
  const [heimAuswaerts, setHeimAuswaerts] = useState("heim");
  const [toreEigen, setToreEigen] = useState("");
  const [toreGegner, setToreGegner] = useState("");

  const submit = async () => {
    if (!gegner.trim()) { toast.error("Bitte Gegner angeben"); return; }
    if (!datum) { toast.error("Bitte Datum wählen"); return; }
    setSaving(true);
    try {
      const payload = {
        datum,
        gegner: gegner.trim(),
        spieltyp,
        heim_auswaerts: heimAuswaerts,
        tore_eigen: toreEigen === "" ? null : Number(toreEigen),
        tore_gegner: toreGegner === "" ? null : Number(toreGegner),
      };
      const { data } = await api.post("/matches", payload);
      toast.success("Spiel angelegt");
      navigate(`/noten/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <button onClick={() => navigate("/noten")}
        className="text-xs text-zinc-400 hover:text-brand-lighter transition flex items-center gap-1 mb-5"
        data-testid="mc-back-btn">
        <ArrowLeft className="h-3 w-3" /> Zurück
      </button>

      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-wider text-brand-lighter mb-1">Neues Spiel</div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-zinc-100">Match anlegen</h1>
      </div>

      <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-5 sm:p-6 space-y-5">
        <Field label="Spieltyp">
          <Pills value={spieltyp} onChange={setSpieltyp} options={SPIELTYPEN} testIdPrefix="mc-spieltyp" />
        </Field>

        <Field label="Datum">
          <Input type="date" value={datum} onChange={(e) => setDatum(e.target.value)}
            className="bg-zinc-950 border-zinc-800 text-zinc-100 focus-visible:ring-brand h-11 rounded-md"
            data-testid="mc-datum" />
        </Field>

        <Field label="Gegner">
          <Input value={gegner} onChange={(e) => setGegner(e.target.value)}
            placeholder="z. B. SV Beispiel"
            className="bg-zinc-950 border-zinc-800 text-zinc-100 focus-visible:ring-brand h-11 rounded-md"
            data-testid="mc-gegner" />
        </Field>

        <Field label="Heim / Auswärts">
          <Pills value={heimAuswaerts} onChange={setHeimAuswaerts}
            options={[{key:"heim",label:"Heim"},{key:"auswaerts",label:"Auswärts"}]}
            testIdPrefix="mc-ha" />
        </Field>

        <Field label="Ergebnis (optional)">
          <div className="flex items-center gap-2">
            <Input type="number" min={0} max={99} value={toreEigen} onChange={(e) => setToreEigen(e.target.value)}
              className="bg-zinc-950 border-zinc-800 text-zinc-100 focus-visible:ring-brand h-11 rounded-md w-20 text-center text-lg font-bold"
              data-testid="mc-tore-eigen" placeholder="—" />
            <span className="text-zinc-500 font-display text-xl">:</span>
            <Input type="number" min={0} max={99} value={toreGegner} onChange={(e) => setToreGegner(e.target.value)}
              className="bg-zinc-950 border-zinc-800 text-zinc-100 focus-visible:ring-brand h-11 rounded-md w-20 text-center text-lg font-bold"
              data-testid="mc-tore-gegner" placeholder="—" />
          </div>
        </Field>

        <Field label="Wäsche mitgenommen von (Trikots · Hosen · Stutzen)">
          <div className="flex items-start gap-2 rounded-md border border-zinc-800 bg-zinc-950/60 p-3 text-xs text-zinc-400" data-testid="mc-waesche-hint">
            <Shirt className="h-4 w-4 text-brand-lighter shrink-0 mt-0.5" />
            <span>
              Der Wäschedienst wird nach der Notenvergabe im Spiel-Detail festgelegt.
              Nur Spieler mit Spielernote stehen dort zur Auswahl.
            </span>
          </div>
        </Field>

        <Button onClick={submit} disabled={saving}
          className="w-full bg-brand hover:bg-brand-light text-white font-semibold rounded-md h-11"
          data-testid="mc-save-btn">
          {saving ? "Speichern…" : "Spiel anlegen"}
        </Button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-zinc-300">{label}</Label>
      {children}
    </div>
  );
}

function Pills({ value, onChange, options, testIdPrefix }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <button key={opt.key} type="button" onClick={() => onChange(opt.key)}
          className={
            "min-h-[44px] px-4 rounded-md border text-sm font-medium transition flex-1 sm:flex-none " +
            (value === opt.key
              ? "bg-brand border-brand text-white"
              : "bg-zinc-950 border-zinc-800 text-zinc-300 hover:border-brand-lighter/60 hover:text-zinc-100")
          }
          data-testid={`${testIdPrefix}-${opt.key}`}>
          {opt.label}
        </button>
      ))}
    </div>
  );
}
